import type { WritableAtom } from "jotai";
import { healthAreaAtom, goalAtom, routineAtom, type ResourceState } from "@/state/resources";
import { SCHEMA_NAMES, type SchemaNames } from "instantdb/types";
import { PATHS } from "@/config/paths";
import { useTranslation } from "@/hooks/useTranslation";

type Labels = {
  singular: string;
  plural: string;
};

type TranslationKeys = {
  addTitle: string;
  editTitle: string;
  createDescription: string;
  updateDescription: string;
};

type ResourceInfo = {
  resourceName: string;
  labels: Labels;
  atom: WritableAtom<ResourceState, [ResourceState], void>;
  path: string;
  pathManage: string;
  translationKeys: TranslationKeys;
};

/**
 * Determines the labels, atom, translation keys, and paths for a slug (Goal, Routine, or Health Area)
 * @param slug - The slug to analyze
 * @returns Object containing labels, atom, path, pathManage, and translationKeys
 */
export const getResourceInfo = (slug: SchemaNames): ResourceInfo => {
  const { t } = useTranslation();

  const getTranslationKeys = (slug: SchemaNames): TranslationKeys => {
    switch (slug) {
      case SCHEMA_NAMES.healthAreas:
        return {
          addTitle: "manage_health_area.add_title",
          editTitle: "manage_health_area.edit_title",
          createDescription: "manage_health_area.create_description",
          updateDescription: "manage_health_area.update_description",
        };
      case SCHEMA_NAMES.goals:
        return {
          addTitle: "manage_goal.add_title",
          editTitle: "manage_goal.edit_title",
          createDescription: "manage_goal.create_description",
          updateDescription: "manage_goal.update_description",
        };
      case SCHEMA_NAMES.routines:
        return {
          addTitle: "manage_routine.add_title",
          editTitle: "manage_routine.edit_title",
          createDescription: "manage_routine.create_description",
          updateDescription: "manage_routine.update_description",
        };
      default:
        throw new Error("Invalid slug");
    }
  };

  switch (slug) {
    case SCHEMA_NAMES.healthAreas:
      return {
        resourceName: "healthArea",
        labels: {
          singular: t("resource_labels.health_area.singular"),
          plural: t("resource_labels.health_area.plural"),
        },
        atom: healthAreaAtom,
        path: PATHS.HEALTH_AREAS,
        pathManage: PATHS.HEALTH_AREA_MANAGE,
        translationKeys: getTranslationKeys(slug),
      };
    case SCHEMA_NAMES.goals:
      return {
        resourceName: "goal",
        labels: {
          singular: t("resource_labels.goal.singular"),
          plural: t("resource_labels.goal.plural"),
        },
        atom: goalAtom,
        path: PATHS.GOALS,
        pathManage: PATHS.GOAL_MANAGE,
        translationKeys: getTranslationKeys(slug),
      };
    case SCHEMA_NAMES.routines:
      return {
        resourceName: "routine",
        labels: {
          singular: t("resource_labels.routine.singular"),
          plural: t("resource_labels.routine.plural"),
        },
        atom: routineAtom,
        path: PATHS.ROUTINES,
        pathManage: PATHS.ROUTINE_MANAGE,
        translationKeys: getTranslationKeys(slug),
      };
    default:
      throw new Error("Invalid slug");
  }
};
